import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  dvtSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  buildDataTableCols,
  buildInputTableCols,
  CellFieldV2 as CellField,
  type DataTableCol,
  type DataTableColumn,
  type DataTableRenderCellParams,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import {
  AccountModel,
  BoPhan,
  ChiPhi,
  DotThanhToan,
  HopDong,
  KheUoc,
  Phi,
  type Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import { formatDate, formatMoney, formatNumber, formatRoundedNumber } from '@/lib/formatUtils';
import type { FormMode } from '@/types/form';

type DetailInputColumn = DataTableCol<BanHangRecord> & { pasteConfig?: BanHangValue };
type DetailTableColumn = DataTableColumn & { pasteConfig?: BanHangValue; nonWritable?: boolean };

const buildColumnsWithPasteConfig = (columns: DetailInputColumn[]): DetailTableColumn[] =>
  buildInputTableCols<BanHangRecord>(columns).map((column, index) => ({
    ...column,
    pasteConfig: columns[index]?.pasteConfig
  }));

export const getDataTableColumns = (handleViewClick: (row: BanHangRecord) => void): DataTableColumn[] =>
  buildDataTableCols<BanHangRecord>([
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      renderCell: params => {
        const status = params.row.status;
        switch (status) {
          case '0':
            return 'Chưa ghi sổ';
          case '3':
            return 'Chờ duyệt';
          case '5':
            return 'Đã ghi sổ';
          case '9':
            return 'Hủy';
          default:
            return 'Hủy';
        }
      }
    },
    {
      field: 'ma_tthddt',
      headerName: 'Trạng thái HĐĐT',
      width: 150,
      renderCell: params => (params.row.ma_tthddt == '0' ? 'Không sử dụng' : 'Chờ phát hành')
    },
    {
      field: 'so_ct',
      headerName: 'Số c/từ',
      width: 150,
      renderCell: params => (
        <button
          type='button'
          onClick={e => {
            e.stopPropagation();
            handleViewClick(params.row);
          }}
          className='cursor-pointer hover:text-blue-500 hover:underline'
        >
          {params.row.so_ct}
        </button>
      )
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      renderCell: params => formatDate(params.row.ngay_ct)
    },
    {
      field: 'ma_kh_data',
      headerName: 'Mã khách hàng',
      width: 150,
      renderCell: params => params.row.ma_kh_data?.customer_code || ''
    },
    {
      field: 'ten_kh',
      headerName: 'Tên khách hàng',
      width: 200,
      renderCell: params => params.row.ma_kh_data?.customer_name || ''
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 250
    },
    {
      field: 'tk',
      headerName: 'Tk có',
      width: 120,
      renderCell: params => params.row.tk_data?.code
    },
    {
      field: 't_tt_nt',
      headerName: 'Tổng tiền',
      width: 150,
      columnType: 'number',
      renderCell: params => formatRoundedNumber(params.row.t_tt_nt)
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 120,
      renderCell: params => params.row.ma_nt_data?.ma_nt
    },
    {
      field: 'ma_ngv',
      headerName: 'Loại hóa đơn',
      width: 120,
      renderCell: params => {
        const status = params.row.ma_ngv;
        switch (status) {
          case '1':
            return '1. Giảm giá';
          case '2':
            return '2. Trả lại';
          case '3':
            return '3. Chiếu khấu cuối kỳ';
          default:
            return '3. Chiếu khấu cuối kỳ';
        }
      }
    }
  ]);

export const getDetailTableColumns = (
  formMode: FormMode,
  onCellValueChange?: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  dienGiai?: string,
  cellError?: (rowUuid: string, field: string) => string | undefined
): DetailTableColumn[] => {
  const isViewMode = formMode === 'view';

  return buildColumnsWithPasteConfig([
    {
      field: 'tk_no',
      headerName: 'Tk nợ',
      width: 150,
      renderCell: params => (
        <SearchField<AccountModel>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_no_data?.code || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'tk_no_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_no',
        resultField: 'tk_no_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'ten_tk',
      headerName: 'Tên tài khoản',
      width: 200,
      renderCell: params => <CellField disabled name='ten_tk' type='text' value={params.row.tk_no_data?.name || ''} />,
      nonWritable: true
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 150,
      renderCell: params => (
        <SearchField<BanHangRecord>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DON_VI_TINH}/`}
          searchColumns={dvtSearchColumns}
          columnDisplay='dvt'
          dialogTitle='Danh mục đơn vị tính'
          value={params.row.dvt_data?.dvt || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'dvt_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'dvt',
        resultField: 'dvt_data',
        detailEndpoint: QUERY_KEYS.DON_VI_TINH
      }
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 180,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='so_luong'
          type='number'
          value={params.row.so_luong}
          placeholder='0'
          onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'so_luong', newValue)}
        />
      )
    },
    {
      field: 'gia_nt2',
      headerName: 'Giá VND',
      width: 120,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='gia_nt2'
          type='number'
          value={params.row.gia_nt2 || null}
          placeholder='0'
          onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'gia_nt2', newValue)}
        />
      )
    },
    {
      field: 'tien_nt2',
      headerName: 'Tiền VND',
      width: 180,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='tien_nt2'
          type='number'
          value={params.row.tien_nt2 || 0}
          placeholder='0'
          onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'tien_nt2', newValue)}
        />
      )
    },
    {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 190,
      renderCell: params => (
        <SearchField<Tax>
          disabled={isViewMode}
          searchEndpoint={`/${QUERY_KEYS.THUE}/`}
          searchColumns={thueSearchColumns}
          columnDisplay='ten_thue'
          dialogTitle='Danh mục thuế suất'
          value={params.row.ma_thue_data?.ten_thue || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_thue',
        resultField: 'ma_thue_data',
        detailEndpoint: QUERY_KEYS.THUE
      }
    },
    {
      field: 'tk_thue_no',
      headerName: 'Tk thuế',
      width: 120,
      renderCell: params => (
        <SearchField<AccountModel>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_thue_no_data?.code || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'tk_thue_no_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_thue_no',
        resultField: 'tk_thue_no_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'thue_nt',
      headerName: 'Thuế VND',
      width: 180,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='thue_nt'
          type='number'
          value={params.row.thue_nt}
          onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'thue_nt', newValue)}
        />
      )
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 250,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='dien_giai'
          type='text'
          value={params.row.dien_giai || dienGiai}
          onValueChange={newValue => onCellValueChange?.(params.row.uuid, 'dien_giai', newValue)}
        />
      )
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: params => (
        <SearchField<BoPhan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          dialogTitle='Danh mục bộ phận'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_bp_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_bp')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: params => (
        <SearchField<VuViec>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
          searchColumns={vuViecSearchColumns}
          columnDisplay='ma_vu_viec'
          dialogTitle='Danh mục vụ việc'
          value={params.row.ma_vv_data?.ma_vu_viec || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_vv_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_vv')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vv',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: params => (
        <SearchField<HopDong>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
          searchColumns={hopDongSearchColumns}
          columnDisplay='ma_hd'
          dialogTitle='Danh mục hợp đồng'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_hd_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_hd')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: params => (
        <SearchField<DotThanhToan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
          searchColumns={dotThanhToanSearchColumns}
          columnDisplay='ma_dtt'
          dialogTitle='Danh mục đợt thanh toán'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_dtt_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_dtt')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: params => (
        <SearchField<KheUoc>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
          searchColumns={kheUocSearchColumns}
          columnDisplay='ma_ku'
          dialogTitle='Danh mục khế ước'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_ku_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_ku')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 100,
      renderCell: params => (
        <SearchField<Phi>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}/`}
          searchColumns={phiSearchColumns}
          columnDisplay='ma_phi'
          dialogTitle='Danh mục phí'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_phi_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_phi')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_vt',
      headerName: 'Sản phẩm',
      width: 120,
      renderCell: params => (
        <SearchField<VatTu>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục sản phẩm'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_sp_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_sp',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 150,
      renderCell: params => (
        <SearchField<BanHangRecord>
          disabled
          type='text'
          searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
          searchColumns={lenhSanXuatSearchColumns}
          columnDisplay='ma_lsx'
          dialogTitle='Danh mục lệnh sản xuất'
          value={params.row.ma_lsx_data?.ma_lsx || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_lsx_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_lsx')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_lsx',
        resultField: 'ma_lsx_data',
        detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
      }
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: params => (
        <SearchField<ChiPhi>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          columnDisplay='ma_cp'
          dialogTitle='Danh mục chi phí không hợp lệ'
          value={params.row.ma_cp0_data?.ma_cp || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_cp0_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_cp0')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp0',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'so_ct_dh',
      headerName: 'Số hóa đơn',
      width: 180,
      renderCell: params => params.row.id_hd?.so_ct
    },
    {
      field: 'line_dh',
      headerName: 'Dòng HĐ',
      width: 120,
      columnType: 'number',
      renderCell: params => params.row.line_hd?.line
    }
  ]);
};

export const detailPreviewColumns: DataTableColumn[] = buildInputTableCols<BanHangRecord>([
  {
    field: 'tk_no',
    headerName: 'Tk nợ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_no_data?.code
  },
  {
    field: 'ten_tk',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_no_data?.name
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 180,
    columnType: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatNumber(params.row.so_luong)
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá VND',
    width: 120,
    columnType: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatMoney(params.row.gia_nt2)
  },
  {
    field: 'tien_nt2',
    headerName: 'Tiền VND',
    width: 180,
    columnType: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatMoney(params.row.tien_nt2)
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 190,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_thue_data?.ten_thue
  },
  {
    field: 'tk_thue_no',
    headerName: 'Tk thuế',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_thue_no_data?.code
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 180,
    columnType: 'number',
    renderCell: (params: DataTableRenderCellParams) => formatMoney(params.row.thue_nt)
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 250
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vv_data?.ma_vv
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_vt',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_lsx_data?.ma_lsx
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_cp0_data?.ma_cpkhl
  }
]);

export const historyColumns: DataTableColumn[] = buildDataTableCols([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
